// ShareService.js — generación de tarjetas compartibles para Concordare
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

const FONT = '"Segoe UI", "Helvetica Neue", Arial, sans-serif';

// Colores idénticos a PODIUM_COLORS de ResultsTable.jsx
const SCORE_COLORS = {
  5: '#FFD700',  // Oro   — 1.ª posición
  4: '#C0C0C0',  // Plata — 2.ª posición
  3: '#CD7F32',  // Bronce — 3.ª posición
  2: '#78909c',  // Gris azulado — 4.ª posición
  1: '#546e7a',  // Gris oscuro — 5.ª posición
  0: '#37474f',  // Gris muy oscuro — sin puntos
};

// Color del texto sobre cada fondo (oro y plata son claros → texto oscuro)
const SCORE_TEXT_COLORS = {
  5: '#7a5100',
  4: '#3a3a3a',
  3: '#ffffff',
  2: '#ffffff',
  1: '#ffffff',
  0: 'rgba(255,255,255,0.45)',
};

const SCORE_EMOJIS = {
  5: '🥇',
  4: '🥈',
  3: '🥉',
  2: '🔘',
  1: '🔘',
  0: '⬜',
};

// ─── Utilidades de canvas ──────────────────────────────────────────────────

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function drawScoreSquare(ctx, x, y, size, score) {
  ctx.save();
  roundRect(ctx, x, y, size, size, 7);
  ctx.fillStyle = SCORE_COLORS[score];
  ctx.fill();
  ctx.fillStyle = SCORE_TEXT_COLORS[score];
  ctx.font = `bold ${Math.round(size * 0.42)}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(score > 0 ? String(score) : '—', x + size / 2, y + size / 2 + 1);
  ctx.restore();
}

function drawNumberCircle(ctx, x, y, size, num, color = '#1976d2') {
  ctx.save();
  ctx.beginPath();
  ctx.arc(x + size / 2, y + size / 2, size / 2, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.fillStyle = 'white';
  ctx.font = `bold ${Math.round(size * 0.42)}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(String(num), x + size / 2, y + size / 2 + 1);
  ctx.restore();
}

function formatDateShort(isoDateStr) {
  if (!isoDateStr) return '';
  const parts = String(isoDateStr).split('-');
  if (parts.length !== 3) return isoDateStr;
  return `${parts[2]}/${parts[1]}/${parts[0]}`;
}

function drawLogoHeader(ctx, W, logoY = 58) {
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = '#4da3ff';
  ctx.font = `bold 30px ${FONT}`;
  ctx.fillText('CONCORDARE', W / 2, logoY);
  ctx.fillStyle = '#764ba2';
  ctx.fillRect(W / 2 - 68, logoY + 7, 136, 3);
}

function drawDarkBackground(ctx, W, H) {
  ctx.fillStyle = '#1a1a2e';
  ctx.fillRect(0, 0, W, H);
  const grad = ctx.createRadialGradient(W / 2, H / 2, 0, W / 2, H / 2, W * 0.7);
  grad.addColorStop(0, 'rgba(25,118,210,0.13)');
  grad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, W, H);
}

// ─── Lógica de negocio ────────────────────────────────────────────────────

// userWords: array de { raw, canonical } — se compara por canonical (fiable aunque la grafia
// difiera de la que se muestra en finalResults), pero se muestra siempre el raw del propio usuario.
function computeWordResults(userWords, finalResults) {
  const resultMap = new Map(
    (finalResults || []).map(r => [r.word.toLowerCase(), r])
  );
  return (userWords || []).map(uw => {
    const key = (uw.canonical || uw.raw || '').toLowerCase();
    const match = resultMap.get(key);
    return {
      word: uw.raw ?? uw,
      score: match ? match.score : 0,
      amount: match ? match.amount : 0,
      position: match ? match.position : null,
    };
  });
}

function computeTotalScore(wordResults) {
  return (wordResults || []).reduce((sum, r) => sum + (r.score || 0), 0);
}

// Porcentaje medio de coincidencia: promedio de (amount/playersToday) de las palabras que puntuaron.
// Devuelve null si no hay datos suficientes.
function computeCoincidencePercent(wordResults, playersToday) {
  if (!playersToday || playersToday <= 0) return null;
  const scored = (wordResults || []).filter(r => r.score > 0 && r.amount > 0);
  if (scored.length === 0) return null;
  const avg = scored.reduce((sum, r) => sum + r.amount / playersToday, 0) / scored.length;
  return Math.round(avg * 100);
}

function buildClipboardText({ topic, topicDate, wordResults, totalScore, playersLabel, playersToday }) {
  const dateStr = formatDateShort(topicDate);
  const emojiRow = (wordResults || []).map(r => SCORE_EMOJIS[r.score] || '⬜').join('');
  const coincidence = computeCoincidencePercent(wordResults, playersToday);
  const lines = [
    'CONCORDARE 🎯',
    `Tema: ${topic} (${dateStr})`,
    emojiRow,
    `${totalScore}/15 pts${playersLabel ? ` · ${playersLabel}` : ''}`,
  ];
  if (coincidence !== null) lines.push(`Coincidí con el ${coincidence}% de los jugadores 🤝`);
  lines.push('concordare.es');
  return lines.join('\n');
}

function buildPendingClipboardText({ topic, topicDate, userWords, playersLabel }) {
  const dateStr = formatDateShort(topicDate);
  const dots = (userWords || []).map(() => '🔵').join('');
  return [
    'CONCORDARE 🎯',
    `Tema: ${topic} (${dateStr})`,
    dots,
    `${playersLabel ? `${playersLabel} ya han participado` : ''}`,
    '¿Qué palabras habrás pensado? 👀',
    'concordare.es',
  ].join('\n');
}

// ─── Tarjeta compacta de resultados (600×600) — palabras visibles ─────────

function drawCompactCard({ topic, topicDate, wordResults, totalScore, playersToday, coincidence }) {
  const W = 600, H = 600;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  drawDarkBackground(ctx, W, H);
  drawLogoHeader(ctx, W, 58);

  // Divisor
  ctx.fillStyle = 'rgba(255,255,255,0.13)';
  ctx.fillRect(40, 80, W - 80, 1);

  // Tema
  const topicText = (topic || '?').toUpperCase();
  const topicFs = topicText.length > 15 ? 20 : topicText.length > 10 ? 24 : topicText.length > 7 ? 27 : 30;
  ctx.fillStyle = 'white';
  ctx.font = `bold ${topicFs}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(topicText, W / 2, 110);

  // Fecha
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = `14px ${FONT}`;
  ctx.fillText(formatDateShort(topicDate), W / 2, 132);

  // Filas de palabras — visibles + score
  const ROW_START_Y = 152;
  const ROW_H = 50;
  const SQ = 30;
  const SQ_X = 42;
  const TEXT_X = SQ_X + SQ + 14;
  const TEXT_MAX_W = 310;
  const BADGE_RIGHT_X = W - 42;

  const rows = Array.from({ length: 5 }, (_, i) => wordResults?.[i] ?? null);
  rows.forEach((wr, i) => {
    const rowY = ROW_START_Y + i * ROW_H;
    const midY = rowY + ROW_H / 2;
    const sqY = midY - SQ / 2;

    if (wr) {
      // Score square
      drawScoreSquare(ctx, SQ_X, sqY, SQ, wr.score);

      // Row background
      ctx.save();
      roundRect(ctx, SQ_X + SQ + 8, rowY + 6, W - SQ_X - SQ - 8 - 40, ROW_H - 12, 8);
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.fill();
      ctx.restore();

      // Word text
      const wordDisplay = wr.word
        ? wr.word.charAt(0).toUpperCase() + wr.word.slice(1).toLowerCase()
        : '';
      ctx.fillStyle = 'white';
      ctx.font = `bold 17px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.save();
      ctx.beginPath();
      ctx.rect(TEXT_X, rowY, TEXT_MAX_W, ROW_H);
      ctx.clip();
      ctx.fillText(wordDisplay, TEXT_X, midY);
      ctx.restore();

      // Score badge (right)
      const scoreText = wr.score > 0 ? `+${wr.score}` : '0';
      const badgeColor = wr.score > 0 ? SCORE_COLORS[wr.score] : 'rgba(255,255,255,0.2)';
      ctx.save();
      const bW = 44, bH = 24;
      roundRect(ctx, BADGE_RIGHT_X - bW, midY - bH / 2, bW, bH, 12);
      ctx.fillStyle = badgeColor;
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = wr.score > 0 ? SCORE_TEXT_COLORS[wr.score] : 'rgba(255,255,255,0.7)';
      ctx.font = `bold 13px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(scoreText, BADGE_RIGHT_X - bW / 2, midY);
    } else {
      // Empty row
      ctx.save();
      roundRect(ctx, SQ_X, rowY + 6, W - SQ_X - 40, ROW_H - 12, 8);
      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.font = `14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('—', W / 2, midY);
    }
  });

  // Divisor antes del score
  const DIV_Y = ROW_START_Y + 5 * ROW_H + 10;
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fillRect(40, DIV_Y, W - 80, 1);

  // Score total
  const SCORE_Y = DIV_Y + 20;
  const scoreColor = totalScore >= 12 ? '#22c55e' : totalScore >= 6 ? '#eab308' : 'white';
  ctx.font = `bold 56px ${FONT}`;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = scoreColor;
  ctx.textAlign = 'right';
  ctx.fillText(String(totalScore), W / 2 - 6, SCORE_Y + 52);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = `bold 28px ${FONT}`;
  ctx.textAlign = 'left';
  ctx.fillText('/ 15', W / 2 + 6, SCORE_Y + 52);
  ctx.fillStyle = 'rgba(255,255,255,0.4)';
  ctx.font = `14px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.fillText('puntos', W / 2, SCORE_Y + 72);

  // Jugadores
  if (playersToday) {
    ctx.fillStyle = 'rgba(255,255,255,0.35)';
    ctx.font = `13px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.fillText(`${playersToday} jugadores participaron hoy`, W / 2, SCORE_Y + 94);
  }

  // Coincidencia
  if (coincidence !== null && coincidence !== undefined) {
    const coinY = playersToday ? SCORE_Y + 116 : SCORE_Y + 94;
    // Pill de coincidencia
    const pillW = 268, pillH = 26;
    ctx.save();
    roundRect(ctx, W / 2 - pillW / 2, coinY - 16, pillW, pillH, 13);
    ctx.fillStyle = 'rgba(77,163,255,0.18)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(77,163,255,0.4)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = '#4da3ff';
    ctx.font = `bold 12px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(`🤝  Coincidí con el ${coincidence}% de los jugadores`, W / 2, coinY - 3);
  }

  // URL footer
  ctx.fillStyle = '#4da3ff';
  ctx.font = `13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('concordare.es', W / 2, H - 18);

  return canvas;
}

// ─── Tarjeta Story de resultados (540×960) — palabras visibles ────────────

function drawStoryCard({ topic, topicDate, wordResults, totalScore, playersToday, coincidence }) {
  const W = 540, H = 960;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Fondo gradiente
  const bg = ctx.createLinearGradient(0, 0, W * 0.4, H);
  bg.addColorStop(0, '#667eea');
  bg.addColorStop(1, '#764ba2');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Logo
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'white';
  ctx.font = `bold 36px ${FONT}`;
  ctx.fillText('CONCORDARE', W / 2, 82);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.fillRect(W / 2 - 68, 90, 136, 2);

  // Tagline
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = `16px ${FONT}`;
  ctx.fillText('el juego de palabras diario', W / 2, 116);

  // Fecha badge
  const dateStr = formatDateShort(topicDate);
  const DW = 148, DH = 30, DX = W / 2 - 74, DY = 134;
  ctx.save();
  roundRect(ctx, DX, DY, DW, DH, 15);
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = 'white';
  ctx.font = `13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(dateStr, W / 2, DY + DH / 2);

  // Tema
  const topicText = (topic || '?').toUpperCase();
  const topicFs = topicText.length > 16 ? 34 : topicText.length > 12 ? 40 : topicText.length > 8 ? 46 : 52;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'white';
  ctx.font = `bold ${topicFs}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillText(topicText, W / 2, 234);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Tarjeta blanca
  const CX = 28, CY = 258, CW = W - 56, CH = 422;
  ctx.save();
  roundRect(ctx, CX, CY, CW, CH, 24);
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.shadowColor = 'rgba(0,0,0,0.22)';
  ctx.shadowBlur = 22;
  ctx.shadowOffsetY = 8;
  ctx.fill();
  ctx.restore();

  // Filas de palabras — visibles
  const WR_START = CY + 22;
  const WR_H = 60;
  const PILL_PX = CX + 18;
  const PILL_PW = CW - 36;
  const PILL_PH = 46;

  const rows = Array.from({ length: 5 }, (_, i) => wordResults?.[i] ?? null);
  rows.forEach((wr, i) => {
    const rowY = WR_START + i * WR_H;
    const pillY = rowY + (WR_H - PILL_PH) / 2;

    ctx.save();
    roundRect(ctx, PILL_PX, pillY, PILL_PW, PILL_PH, 23);
    ctx.fillStyle = wr ? SCORE_COLORS[wr.score] + 'D0' : 'rgba(0,0,0,0.06)';
    ctx.fill();
    ctx.restore();

    if (wr) {
      const wordDisplay = wr.word
        ? wr.word.charAt(0).toUpperCase() + wr.word.slice(1).toLowerCase()
        : '';
      ctx.fillStyle = SCORE_TEXT_COLORS[wr.score];
      ctx.font = `bold 18px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(wordDisplay, PILL_PX + 20, pillY + PILL_PH / 2);

      // Badge de score
      const BR = 19;
      const BX = PILL_PX + PILL_PW - BR - 10;
      const BY = pillY + PILL_PH / 2;
      ctx.save();
      ctx.beginPath();
      ctx.arc(BX, BY, BR, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,0.28)';
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = SCORE_TEXT_COLORS[wr.score];
      ctx.font = `bold 14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(wr.score > 0 ? `+${wr.score}` : '0', BX, BY);
    } else {
      ctx.fillStyle = 'rgba(0,0,0,0.2)';
      ctx.font = `14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('—', PILL_PX + PILL_PW / 2, pillY + PILL_PH / 2);
    }
  });

  // Divisor y score en tarjeta
  const hasCoincidence = coincidence !== null && coincidence !== undefined;
  const DIV2_Y = CY + CH - (hasCoincidence ? 90 : 68);
  ctx.fillStyle = 'rgba(0,0,0,0.09)';
  ctx.fillRect(CX + 18, DIV2_Y, CW - 36, 1);
  ctx.fillStyle = '#1a1a2e';
  ctx.font = `bold 20px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(`${totalScore} / 15 puntos`, W / 2, DIV2_Y + 26);

  if (hasCoincidence) {
    ctx.fillStyle = '#1976d2';
    ctx.font = `bold 14px ${FONT}`;
    ctx.fillText(`🤝 Coincidí con el ${coincidence}% de los jugadores`, W / 2, DIV2_Y + 54);
  }

  // Score héroe
  const HERO_Y = CY + CH + 26;
  ctx.fillStyle = '#FFD700';
  ctx.font = `bold 80px ${FONT}`;
  ctx.textAlign = 'right';
  ctx.textBaseline = 'alphabetic';
  ctx.shadowColor = 'rgba(0,0,0,0.3)';
  ctx.shadowBlur = 12;
  ctx.shadowOffsetY = 4;
  ctx.fillText(String(totalScore), W / 2 - 4, HERO_Y + 72);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.fillStyle = 'rgba(255,255,255,0.7)';
  ctx.font = `bold 32px ${FONT}`;
  ctx.textAlign = 'left';
  ctx.fillText('/ 15', W / 2 + 8, HERO_Y + 72);

  if (playersToday) {
    ctx.fillStyle = 'rgba(255,255,255,0.65)';
    ctx.font = `15px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(`${playersToday} jugadores participaron hoy`, W / 2, HERO_Y + 100);
  }

  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = `13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.fillText('#concordare  ·  concordare.es', W / 2, H - 28);

  return canvas;
}

// ─── Tarjeta compacta pendiente (600×600) ─────────────────────────────────

function drawPendingCompactCard({ topic, topicDate, userWords, playersToday, revealWords }) {
  const W = 600, H = 600;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  drawDarkBackground(ctx, W, H);
  drawLogoHeader(ctx, W, 58);

  // Divisor
  ctx.fillStyle = 'rgba(255,255,255,0.13)';
  ctx.fillRect(40, 80, W - 80, 1);

  // Tema
  const topicText = (topic || '?').toUpperCase();
  const topicFs = topicText.length > 15 ? 20 : topicText.length > 10 ? 24 : topicText.length > 7 ? 27 : 30;
  ctx.fillStyle = 'white';
  ctx.font = `bold ${topicFs}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(topicText, W / 2, 110);

  // Fecha
  ctx.fillStyle = 'rgba(255,255,255,0.5)';
  ctx.font = `14px ${FONT}`;
  ctx.fillText(formatDateShort(topicDate), W / 2, 132);

  // Etiqueta de estado pendiente
  const labelW = 180, labelH = 26;
  ctx.save();
  roundRect(ctx, W / 2 - labelW / 2, 140, labelW, labelH, 13);
  ctx.fillStyle = 'rgba(255,193,7,0.2)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,193,7,0.5)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = '#ffc107';
  ctx.font = `bold 12px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('⏳  Resultados pendientes', W / 2, 153);

  // Filas de palabras
  const ROW_START_Y = 184;
  const ROW_H = 50;
  const SQ = 30;
  const SQ_X = 42;
  const TEXT_X = SQ_X + SQ + 14;
  const TEXT_MAX_W = 340;
  const BADGE_RIGHT_X = W - 42;

  const rows = Array.from({ length: 5 }, (_, i) => userWords?.[i] ?? null);
  rows.forEach((uw, i) => {
    const rowY = ROW_START_Y + i * ROW_H;
    const midY = rowY + ROW_H / 2;
    const sqY = midY - SQ / 2;
    const word = uw != null ? (uw.raw ?? uw) : null;

    if (word != null) {
      // Círculo numerado (orden de envío)
      drawNumberCircle(ctx, SQ_X, sqY, SQ, i + 1);

      // Fondo de fila
      ctx.save();
      roundRect(ctx, SQ_X + SQ + 8, rowY + 6, W - SQ_X - SQ - 8 - 40, ROW_H - 12, 8);
      ctx.fillStyle = 'rgba(255,255,255,0.06)';
      ctx.fill();
      ctx.restore();

      // Palabra (difuminada o visible)
      const wordDisplay = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      ctx.save();
      ctx.beginPath();
      ctx.rect(TEXT_X, rowY, TEXT_MAX_W, ROW_H);
      ctx.clip();
      if (!revealWords) {
        ctx.filter = 'blur(7px)';
      }
      ctx.fillStyle = 'white';
      ctx.font = `bold 17px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(wordDisplay, TEXT_X, midY);
      ctx.restore();

      // Badge "?" en la derecha
      const bW = 30, bH = 24;
      ctx.save();
      roundRect(ctx, BADGE_RIGHT_X - bW, midY - bH / 2, bW, bH, 12);
      ctx.fillStyle = 'rgba(255,255,255,0.15)';
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = `bold 14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('?', BADGE_RIGHT_X - bW / 2, midY);
    } else {
      // Fila vacía
      ctx.save();
      roundRect(ctx, SQ_X, rowY + 6, W - SQ_X - 40, ROW_H - 12, 8);
      ctx.fillStyle = 'rgba(255,255,255,0.03)';
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = 'rgba(255,255,255,0.12)';
      ctx.font = `14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('—', W / 2, midY);
    }
  });

  // Divisor
  const DIV_Y = ROW_START_Y + 5 * ROW_H + 10;
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.fillRect(40, DIV_Y, W - 80, 1);

  // Contador de jugadores (dato principal)
  const COUNT_Y = DIV_Y + 18;
  if (playersToday != null) {
    ctx.fillStyle = '#4da3ff';
    ctx.font = `bold 44px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(String(playersToday), W / 2, COUNT_Y + 44);
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = `15px ${FONT}`;
    ctx.fillText('jugadores ya han enviado sus palabras', W / 2, COUNT_Y + 66);
  }

  // URL footer
  ctx.fillStyle = '#4da3ff';
  ctx.font = `13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('concordare.es', W / 2, H - 18);

  return canvas;
}

// ─── Tarjeta Story pendiente (540×960) ────────────────────────────────────

function drawPendingStoryCard({ topic, topicDate, userWords, playersToday, revealWords }) {
  const W = 540, H = 960;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // Fondo gradiente (tonos más fríos para indicar "pendiente")
  const bg = ctx.createLinearGradient(0, 0, W * 0.4, H);
  bg.addColorStop(0, '#4a6fa5');
  bg.addColorStop(1, '#6a4c93');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Logo
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'white';
  ctx.font = `bold 36px ${FONT}`;
  ctx.fillText('CONCORDARE', W / 2, 82);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.fillRect(W / 2 - 68, 90, 136, 2);

  // Tagline
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.font = `16px ${FONT}`;
  ctx.fillText('el juego de palabras diario', W / 2, 116);

  // Fecha badge
  const dateStr = formatDateShort(topicDate);
  const DW = 148, DH = 30, DX = W / 2 - 74, DY = 134;
  ctx.save();
  roundRect(ctx, DX, DY, DW, DH, 15);
  ctx.fillStyle = 'rgba(255,255,255,0.2)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.5)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = 'white';
  ctx.font = `13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(dateStr, W / 2, DY + DH / 2);

  // Tema
  const topicText = (topic || '?').toUpperCase();
  const topicFs = topicText.length > 16 ? 34 : topicText.length > 12 ? 40 : topicText.length > 8 ? 46 : 52;
  ctx.textBaseline = 'alphabetic';
  ctx.fillStyle = 'white';
  ctx.font = `bold ${topicFs}px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.shadowColor = 'rgba(0,0,0,0.25)';
  ctx.shadowBlur = 8;
  ctx.shadowOffsetY = 3;
  ctx.fillText(topicText, W / 2, 234);
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetY = 0;

  // Badge de estado pendiente
  const pendW = 210, pendH = 32;
  ctx.save();
  roundRect(ctx, W / 2 - pendW / 2, 248, pendW, pendH, 16);
  ctx.fillStyle = 'rgba(255,193,7,0.25)';
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,193,7,0.6)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.restore();
  ctx.fillStyle = '#ffc107';
  ctx.font = `bold 13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('⏳  Resultados pendientes', W / 2, 264);

  // Tarjeta blanca
  const CX = 28, CY = 296, CW = W - 56, CH = 400;
  ctx.save();
  roundRect(ctx, CX, CY, CW, CH, 24);
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.shadowColor = 'rgba(0,0,0,0.22)';
  ctx.shadowBlur = 22;
  ctx.shadowOffsetY = 8;
  ctx.fill();
  ctx.restore();

  // Filas de palabras
  const WR_START = CY + 22;
  const WR_H = 60;
  const PILL_PX = CX + 18;
  const PILL_PW = CW - 36;
  const PILL_PH = 46;

  const rows = Array.from({ length: 5 }, (_, i) => userWords?.[i] ?? null);
  rows.forEach((uw, i) => {
    const rowY = WR_START + i * WR_H;
    const pillY = rowY + (WR_H - PILL_PH) / 2;
    const word = uw != null ? (uw.raw ?? uw) : null;

    ctx.save();
    roundRect(ctx, PILL_PX, pillY, PILL_PW, PILL_PH, 23);
    ctx.fillStyle = word != null ? 'rgba(25,118,210,0.15)' : 'rgba(0,0,0,0.04)';
    ctx.fill();
    ctx.restore();

    if (word != null) {
      // Número de orden
      drawNumberCircle(ctx, PILL_PX + 8, pillY + (PILL_PH - 28) / 2, 28, i + 1, '#1976d2');

      // Palabra (difuminada o visible)
      const wordDisplay = word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      const wordX = PILL_PX + 48;
      ctx.save();
      ctx.beginPath();
      ctx.rect(wordX, pillY, PILL_PW - 80, PILL_PH);
      ctx.clip();
      if (!revealWords) {
        ctx.filter = 'blur(7px)';
      }
      ctx.fillStyle = '#1a1a2e';
      ctx.font = `bold 17px ${FONT}`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(wordDisplay, wordX, pillY + PILL_PH / 2);
      ctx.restore();

      // Badge "?" derecha
      const BR = 16;
      const BX = PILL_PX + PILL_PW - BR - 10;
      const BY = pillY + PILL_PH / 2;
      ctx.save();
      ctx.beginPath();
      ctx.arc(BX, BY, BR, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(25,118,210,0.15)';
      ctx.fill();
      ctx.restore();
      ctx.fillStyle = '#1976d2';
      ctx.font = `bold 14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('?', BX, BY);
    } else {
      ctx.fillStyle = 'rgba(0,0,0,0.15)';
      ctx.font = `14px ${FONT}`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('—', PILL_PX + PILL_PW / 2, pillY + PILL_PH / 2);
    }
  });

  // Contador héroe (fuera de la tarjeta)
  const HERO_Y = CY + CH + 24;
  if (playersToday != null) {
    ctx.fillStyle = 'white';
    ctx.font = `bold 70px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.shadowColor = 'rgba(0,0,0,0.3)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetY = 3;
    ctx.fillText(String(playersToday), W / 2, HERO_Y + 64);
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(255,255,255,0.75)';
    ctx.font = `16px ${FONT}`;
    ctx.fillText('jugadores ya han enviado sus palabras', W / 2, HERO_Y + 90);
  } else {
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.font = `18px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText('¿Adivinas mis palabras? 👀', W / 2, HERO_Y + 50);
  }

  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = `13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('#concordare  ·  concordare.es', W / 2, H - 28);

  return canvas;
}

// ─── Tarjeta de logro (600×600) ──────────────────────────────────────────

function drawAchievementCard({ emoji, title, subtitle, topic, topicDate }) {
  const W = 600, H = 600;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  drawDarkBackground(ctx, W, H);

  // Acento de color en esquina superior
  const accent = ctx.createLinearGradient(0, 0, W, 180);
  accent.addColorStop(0, 'rgba(118,75,162,0.35)');
  accent.addColorStop(1, 'rgba(25,118,210,0.10)');
  ctx.fillStyle = accent;
  ctx.fillRect(0, 0, W, H);

  drawLogoHeader(ctx, W, 58);

  // Divisor
  ctx.fillStyle = 'rgba(255,255,255,0.13)';
  ctx.fillRect(40, 80, W - 80, 1);

  // Emoji grande centralizado
  ctx.font = `110px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji || '🏆', W / 2, 230);

  // Título del logro con word-wrap
  const titleFs = (title || '').length > 28 ? 22 : (title || '').length > 20 ? 25 : 28;
  ctx.font = `bold ${titleFs}px ${FONT}`;
  ctx.fillStyle = 'white';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';

  const maxW = W - 80;
  const words = (title || '').split(' ');
  const lines = [];
  let current = '';
  words.forEach(word => {
    const test = current ? `${current} ${word}` : word;
    if (ctx.measureText(test).width > maxW && current) {
      lines.push(current);
      current = word;
    } else {
      current = test;
    }
  });
  if (current) lines.push(current);

  const lineH = titleFs + 8;
  const titleStartY = 338 - (lines.length - 1) * lineH / 2;
  lines.forEach((line, i) => {
    ctx.fillText(line, W / 2, titleStartY + i * lineH);
  });

  // Subtítulo
  if (subtitle) {
    ctx.fillStyle = 'rgba(255,255,255,0.58)';
    ctx.font = `16px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(subtitle, W / 2, titleStartY + lines.length * lineH + 14);
  }

  // Pill con el tema del día
  if (topic) {
    const dateStr = topicDate ? `  ·  ${formatDateShort(topicDate)}` : '';
    const pillText = `Tema: ${topic.toUpperCase()}${dateStr}`;
    ctx.font = `bold 13px ${FONT}`;
    const pillW = Math.min(W - 80, ctx.measureText(pillText).width + 48);
    const pillH = 30;
    const pillX = W / 2 - pillW / 2;
    const pillY = 482;
    ctx.save();
    roundRect(ctx, pillX, pillY, pillW, pillH, 15);
    ctx.fillStyle = 'rgba(77,163,255,0.15)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(77,163,255,0.35)';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.restore();
    ctx.fillStyle = '#4da3ff';
    ctx.font = `bold 13px ${FONT}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(pillText, W / 2, pillY + pillH / 2);
  }

  // URL footer
  ctx.fillStyle = '#4da3ff';
  ctx.font = `13px ${FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('concordare.es', W / 2, H - 18);

  return canvas;
}

// Compartir un logro directamente (sin modal)
async function shareAchievement({ emoji, title, subtitle, topic, topicDate }) {
  const canvas = drawAchievementCard({ emoji, title, subtitle, topic, topicDate });
  const fname = `concordare-logro-${String(topicDate || '').replaceAll('-', '') || Date.now()}.png`;
  return shareCard(canvas, fname);
}

// ─── Acciones de compartir ───────────────────────────────────────────────

async function shareCard(canvas, filename) {
  // Generar el archivo de forma SÍNCRONA con toDataURL para preservar el contexto
  // del gesto del usuario — canvas.toBlob() es asíncrono y rompe navigator.share() en móvil
  const dataUrl = canvas.toDataURL('image/png');
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  const blob = new Blob([bytes], { type: 'image/png' });
  const file = new File([blob], filename, { type: 'image/png' });

  // Apps nativas Capacitor (Android / iOS): Filesystem + Share plugin
  if (Capacitor.isNativePlatform()) {
    try {
      await Filesystem.writeFile({ path: filename, data: base64, directory: Directory.Cache });
      const { uri } = await Filesystem.getUri({ path: filename, directory: Directory.Cache });
      await Share.share({ title: 'Concordare', files: [uri] });
      return { method: 'native' };
    } catch (err) {
      if (err?.message?.includes('cancel') || err?.errorMessage?.includes('cancel')) {
        return { method: 'cancelled' };
      }
      // Continuar al fallback web si algo falla en Capacitor
    }
  }

  // Web Share API (navegador)
  if (navigator.share && (!navigator.canShare || navigator.canShare({ files: [file] }))) {
    try {
      await navigator.share({ files: [file], title: 'Concordare' });
      return { method: 'native' };
    } catch (err) {
      if (err.name === 'AbortError') return { method: 'cancelled' };
    }
  }

  // Fallback: descarga directa (escritorio)
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return { method: 'download' };
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fall through */ }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.style.cssText = 'position:fixed;opacity:0;top:0;left:0;pointer-events:none';
  document.body.appendChild(ta);
  ta.focus();
  ta.select();
  try { document.execCommand('copy'); } catch { /* ignore */ }
  document.body.removeChild(ta);
  return true;
}

// ─── Export ──────────────────────────────────────────────────────────────

const shareService = {
  computeWordResults,
  computeTotalScore,
  computeCoincidencePercent,
  buildClipboardText,
  buildPendingClipboardText,
  drawCompactCard,
  drawStoryCard,
  drawPendingCompactCard,
  drawPendingStoryCard,
  drawAchievementCard,
  shareAchievement,
  shareCard,
  copyToClipboard,
  getPreviewDataUrl: canvas => canvas.toDataURL('image/png'),
};

export default shareService;
